/**
 * Institutional authentication shell.
 *
 * Everything drawn before a session exists lives here: the sign-in gate, the
 * failure states the identity provider redirects to, and the verifying state.
 * They share one shell so that a rejected login does not look like a different
 * product than the login itself.
 *
 * These components render OUTSIDE `BrowserRouter` — `App` mounts the router
 * only once a session is proven — so nothing here may use react-router hooks.
 *
 * Colors come from the `--auth-*` tokens in `styles.css`, which default to the
 * structural tokens of the application shell and are overridden by the saved
 * appearance (see `apariencia.ts`). No hex is written here.
 */

import { useEffect, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import backdrop from "../assets/auth-backdrop.webp";

/** The legal entity. Shown above the platform name, never on its own. */
export const ORG_NAME = "BSR, S.A. DE C.V.";

/** The platform. This is the product identity on every screen. */
export const PLATFORM_NAME = "BSR Plataforma institucional";

/**
 * Wordmark: logo plus the two names.
 *
 * Identifying the system BEFORE asking for anything is what separates a real
 * sign-in screen from a copy of one, so it appears twice — on the bar and on
 * the card — and both come from this one component.
 *
 * The logo is `/api/brand/logo`, the only endpoint that serves an image without
 * a session. It never 404s: with no logo uploaded it returns the fallback mark.
 */
export function AuthBrand({ tone }: { tone: "bar" | "card" }) {
  return (
    <div className={`auth-brand auth-brand-${tone}`}>
      <img className="auth-logo" src="/api/brand/logo" alt="" />
      <span className="auth-brand-rule" aria-hidden="true" />
      <span className="auth-brand-names">
        <span className="auth-org">{ORG_NAME}</span>
        <span className="auth-platform">{PLATFORM_NAME}</span>
      </span>
    </div>
  );
}

/**
 * Page frame: institutional bar, two-column body, footer.
 *
 * The card sits in the right column rather than the middle of the viewport. The
 * composition is meant to read as balanced, not symmetrical.
 *
 * The backdrop is painted by `.auth::before` from the `--auth-backdrop` token
 * set here. A pseudo-element and not an `<img>` because the photograph carries
 * no information: it must not reach the accessibility tree, and it has no
 * caption to give it. The URL is set from TypeScript because Vite fingerprints
 * the file at build time, so the stylesheet cannot name it.
 */
export function AuthShell({ context, children }: { context: ReactNode; children: ReactNode }) {
  return (
    <div className="auth" style={{ "--auth-backdrop": `url(${backdrop})` } as CSSProperties}>
      <header className="auth-bar">
        <div className="auth-bar-inner">
          <AuthBrand tone="bar" />
        </div>
      </header>

      <main className="auth-main">
        <div className="auth-grid">
          <section className="auth-context">{context}</section>
          <div className="auth-panel">{children}</div>
        </div>
      </main>

      <footer className="auth-foot">
        <div className="auth-foot-inner">
          <span>
            © {new Date().getFullYear()} {ORG_NAME}
          </span>
          <span className="auth-foot-sep" aria-hidden="true">
            ·
          </span>
          <span>{PLATFORM_NAME}</span>
        </div>
      </footer>
    </div>
  );
}

/**
 * The left column: who this is, and nothing else.
 *
 * Deliberately thin on content. This is an internal platform, so there is
 * nothing to sell here and no feature to advertise; the only job of this column
 * is to say which system the visitor has reached.
 *
 * The `<h1>` lives here and survives every viewport — on narrow screens the
 * supporting lines collapse but the heading does not, so the page is never left
 * without one and never grows a second one.
 */
export function AuthContext() {
  return (
    <>
      <p className="auth-eyebrow">{ORG_NAME}</p>
      <span className="auth-rule" aria-hidden="true" />
      <h1 className="auth-title">{PLATFORM_NAME}</h1>
      <p className="auth-lead">Acceso interno para personal autorizado.</p>
      <p className="auth-note">
        Autenticación centralizada mediante el proveedor de identidad institucional.
      </p>
    </>
  );
}

/**
 * The white card.
 *
 * `tone` marks a state by SHAPE as well as color: it recolors a rule along the
 * top edge, so someone who cannot tell amber from the default still sees that
 * this is not the ordinary sign-in card.
 */
export function AuthCard({
  title,
  tone,
  children,
}: {
  title: string;
  tone?: "warn" | "critical";
  children: ReactNode;
}) {
  return (
    <section className={tone ? `auth-card ${tone}` : "auth-card"}>
      <div className="auth-card-head">
        <AuthBrand tone="card" />
      </div>
      {/* `h2` because the page heading is the platform name in the left
          column, which is present at every width. */}
      <h2 className="auth-card-title">{title}</h2>
      {children}
    </section>
  );
}

/** Quiet strip that says what the button is about to do. */
export function AuthNotice({ children }: { children: ReactNode }) {
  return (
    <p className="auth-notice">
      <InfoGlyph />
      <span>{children}</span>
    </p>
  );
}

/**
 * Verifying / redirecting.
 *
 * Shown while the session is being checked and while the browser is on its way
 * to the identity provider. It uses the same shell as everything else so the
 * page does not flash blank and then rebuild itself.
 *
 * `aria-live` on the status line: for a screen reader the spinner does not
 * exist, and without this the wait would be silent.
 */
export function AuthPending({ what }: { what: string }) {
  return (
    <AuthShell context={<AuthContext />}>
      <AuthCard title="Verificando acceso">
        <p className="auth-body" role="status" aria-live="polite">
          <span className="auth-spinner" aria-hidden="true" />
          {what}
        </p>
      </AuthCard>
    </AuthShell>
  );
}

/**
 * How long a session check may take before the wait is worth drawing.
 *
 * On a reload the check is one same-origin request that comes back in well
 * under this, so nothing is painted at all and the application appears without
 * a flash. Below this threshold the gate was rendering and being replaced
 * within a couple of frames: to the person reloading, an application they were
 * already inside blinked back to a sign-in page.
 *
 * It is not zero, because a slow network must still say something rather than
 * hold a blank page.
 */
const PAINT_THE_WAIT_AFTER_MS = 400;

/** True once the wait has lasted long enough to be worth showing. */
function useSettled(ms: number): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(true), ms);
    return () => window.clearTimeout(timer);
  }, [ms]);
  return settled;
}

/**
 * The wait while the session is being verified.
 *
 * `returning` says this browser has been signed in before, so the wait is
 * drawn as the APPLICATION loading rather than as the gate: someone who is
 * already inside should not be shown a sign-in screen on their way back in.
 *
 * It is a comfort, never a permission. It comes from `localStorage`, which the
 * person can edit, and it changes nothing the browser is allowed to see: the
 * session still has to be verified by the server, and the very next thing this
 * component's caller does with a bad session is show the gate.
 */
export function SessionPending({
  what,
  returning = false,
}: {
  what: string;
  returning?: boolean;
}) {
  const vale = useSettled(PAINT_THE_WAIT_AFTER_MS);
  if (!vale) return null;
  if (!returning) return <AuthPending what={what} />;
  return (
    <div className="auth-espera" role="status" aria-live="polite">
      <span className="auth-spinner" aria-hidden="true" />
      <span>{what}</span>
    </div>
  );
}

/* Inline glyphs. Two shapes do not justify a dependency, and inline they
   inherit `currentColor`, which is what makes them follow the saved
   appearance without a rule of their own. Both are decorative: each sits
   beside text that already carries the meaning. */

function InfoGlyph() {
  return (
    <svg className="auth-glyph" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M8 7.2v4M8 4.9v.1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Leaving this origin. The arrow is the honest signal that a redirect follows. */
export function ExternalGlyph() {
  return (
    <svg className="auth-glyph" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        d="M6.5 3.5H3.2v9.3h9.3V9.5M9.6 3.5h2.9v2.9M12.5 3.5 7.4 8.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
